import React from 'react';
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
    IonApp,
    IonRouterOutlet,
    setupIonicReact,
    createAnimation,
    Animation
} from '@ionic/react';
import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import { KeyboardProvider } from "./components/keyboard/KeyboardProvider";
import { CustomKeyboard } from "./components/keyboard/CustomKeyboard";
import { Provider } from "react-redux";
import { store, persistor } from "./stores";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { AuthGuard } from "./components/AuthGuard";
import { UpdateOfferPrompt } from "./components/UpdateOfferPrompt";
import { NetworkInit } from "./components/NetworkInit";
import { TokenAutoRefresh } from "./components/TokenAutoRefresh";
import { OfflineBanner } from "./components/OfflineBanner";
import { SlowConnectionBanner } from "./components/SlowConnectionBanner";
import { DraftPersistenceErrorListener } from "./components/DraftPersistenceErrorListener";
import { StorageWarning } from "./components/StorageWarning";
import { PageLifecycleHandler } from "./hooks/usePageLifecycle";
import { BASE_URL } from './consts/baseUrl';
import { setAuth, selectCompany } from './stores/authSlice';
import { consumeImpersonationPayload } from './utils/impersonation';
import { SyncProgressProvider } from "./contexts/SyncProgressContext";
import { PWAUpdateProvider } from "./contexts/PWAUpdateContext";
import { SplashScreen } from "./components/SplashScreen";
import { AppReady } from "./components/AppReady";
import { UpdateFlowProvider } from "./contexts/UpdateFlowContext";
import { UpdateFlowInit } from "./components/UpdateFlowInit";
import { UpdateFlowBridge } from "./components/UpdateFlowBridge";
import { NavContextBridge } from "./components/NavContextBridge";
import { WindowControlsOverlaySetup } from "./components/WindowControlsOverlaySetup";
import { ProtocolLinkHandler } from "./components/ProtocolLinkHandler";
import { PWAInstallProvider } from "./contexts/PWAInstallContext";
import { usePreferencesStore } from './features/preferences/store/preferences.store';
import { migrateLegacyUserSettingsToPreferences } from './features/preferences/utils/migrate-legacy-user-settings';
import { Login } from './pages/Login';
import {
    LazyAcceptInvitation,
    LazyAgentShell,
    LazyB2bRootRedirect,
    LazyCompanyAdmin,
    LazyForgotPassword,
    LazyManageUser,
    LazyManageUsers,
    LazyNotFound,
    LazyResetPassword,
    LazySelectCompany,
    LazyStats,
    LazySystemAdminCompanies,
    LazySystemAdminCompanyAgentsRoute,
    LazySystemAdminCompanyForm,
    LazySystemAdminCompanyFormEditRoute,
    LazySystemAdminCompanyUsersRoute,
    LazySystemAdminLayout,
    LazySystemAdminPlaceholder,
} from './routing/lazy-pages';
import { debugWarn } from './utils/debug';

const PERSIST_REHYDRATE_TIMEOUT_MS = 4_000;

/** Evita splash infinito se redux-persist / IndexedDB non risponde. */
const PersistGateWithTimeout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [forceBoot, setForceBoot] = React.useState(false);

    React.useEffect(() => {
        // Il timer va fermato quando la reidratazione riesce, non solo allo
        // unmount: senza questo il warning compariva a ogni boot anche quando
        // il gate si era gia' aperto, mandando fuori strada chi cerca la causa
        // di un avvio lento.
        if (persistor.getState().bootstrapped) {
            return;
        }

        const t = window.setTimeout(() => {
            debugWarn('[PersistGate] rehydrate timeout — forcing app boot');
            setForceBoot(true);
        }, PERSIST_REHYDRATE_TIMEOUT_MS);

        const unsubscribe = persistor.subscribe(() => {
            if (persistor.getState().bootstrapped) {
                window.clearTimeout(t);
                unsubscribe();
            }
        });

        return () => {
            window.clearTimeout(t);
            unsubscribe();
        };
    }, []);

    if (forceBoot) {
        return <>{children}</>;
    }

    return (
        <PersistGate loading={<SplashScreen />} persistor={persistor}>
            {children}
        </PersistGate>
    );
};

// Custom slide animation for page transitions
const slideAnimation = (baseEl: HTMLElement, opts?: any): Animation => {
    const enteringEl = opts?.enteringEl;
    const leavingEl = opts?.leavingEl;
    const direction = opts?.direction || 'forward';

    const enteringAnimation = createAnimation()
        .addElement(enteringEl)
        .duration(300)
        .easing('cubic-bezier(0.36, 0.66, 0.04, 1)')
        .fromTo('transform', direction === 'back' ? 'translateX(-100%)' : 'translateX(100%)', 'translateX(0%)')
        .fromTo('opacity', 0, 1);

    const leavingAnimation = createAnimation()
        .addElement(leavingEl)
        .duration(300)
        .easing('cubic-bezier(0.36, 0.66, 0.04, 1)')
        .fromTo('transform', 'translateX(0%)', direction === 'back' ? 'translateX(100%)' : 'translateX(-100%)')
        .fromTo('opacity', 1, 0);

    return createAnimation()
        .addAnimation([enteringAnimation, leavingAnimation]);
};

setupIonicReact({
    mode: 'md',
    animated: true,
    navAnimation: slideAnimation
});

const TEXT_SCALE_MAP: Record<'medium' | 'large', string> = {
    medium: '1',
    large: '1.25',
};

const LegacyPreferencesMigration: React.FC = () => {
    React.useEffect(() => {
        void migrateLegacyUserSettingsToPreferences();
    }, []);
    return null;
};

const TextScaleSync: React.FC = () => {
    const textSize = usePreferencesStore((s) => s.textSize);
    React.useEffect(() => {
        const scale = TEXT_SCALE_MAP[textSize];
        document.documentElement.style.setProperty('--text-scale', scale);
    }, [textSize]);
    return null;
};

/** Ascolta `db:vfs-strategy` — montato in AgentShell (route agente). */

const TokenRefreshListener: React.FC = () => {
    const queryClient = useQueryClient();
    React.useEffect(() => {
        const onTokenRefreshed = () => {
            queryClient.invalidateQueries();
        };
        window.addEventListener('app:token-refreshed', onTokenRefreshed);
        return () => window.removeEventListener('app:token-refreshed', onTokenRefreshed);
    }, [queryClient]);
    return null;
};

// Telemetry: configurata al mount (non blocca il parse del bundle principale).
const CLIENT_LOGS_URL = `${BASE_URL.replace(/\/$/, '')}/api/client-logs`;

/** Forwards unhandled JS errors and promise rejections to the telemetry service. */
const GlobalErrorHandler: React.FC = () => {
    React.useEffect(() => {
        void import('./services/offline/telemetry/telemetry-service').then(({ telemetryService }) => {
            telemetryService.configure({ endpoint: CLIENT_LOGS_URL, batchSize: 5, flushIntervalMs: 10_000 });
            if (process.env.REACT_APP_TELEMETRY_ENABLED !== 'false') {
                telemetryService.setEnabled(true);
            }
        });
    }, []);

    React.useEffect(() => {
        let telemetryService: typeof import('./services/offline/telemetry/telemetry-service').telemetryService | null = null;
        void import('./services/offline/telemetry/telemetry-service').then((mod) => {
            telemetryService = mod.telemetryService;
        });

        const onError = (event: ErrorEvent) => {
            telemetryService?.trackError(event.error ?? event.message, {
                source: 'window.onerror',
                filename: event.filename,
                lineno: event.lineno,
                colno: event.colno,
            });
        };
        const onUnhandledRejection = (event: PromiseRejectionEvent) => {
            const reason = event.reason as unknown;
            telemetryService?.trackError(
                reason instanceof Error ? reason : String(reason),
                { source: 'unhandledrejection' },
            );
            // Prevent iOS standalone mode from silently reloading the WebView
            // when a promise rejection goes unhandled.
            event.preventDefault();
        };
        window.addEventListener('error', onError);
        window.addEventListener('unhandledrejection', onUnhandledRejection);

        // Memory monitor: log every 30s if JS heap exceeds 100 MB.
        const memTimer = setInterval(() => {
            const mem = (performance as unknown as { memory?: { usedJSHeapSize: number; totalJSHeapSize: number } }).memory;
            if (mem && mem.usedJSHeapSize > 100_000_000) {
                telemetryService?.track({
                    type: 'health_check',
                    data: {
                        source: 'memory_monitor',
                        usedJSHeapSize: mem.usedJSHeapSize,
                        totalJSHeapSize: mem.totalJSHeapSize,
                    },
                });
            }
        }, 30_000);

        return () => {
            window.removeEventListener('error', onError);
            window.removeEventListener('unhandledrejection', onUnhandledRejection);
            clearInterval(memTimer);
        };
    }, []);
    return null;
};

/**
 * Gestisce il flusso di impersonificazione agente all'avvio del tab.
 *
 * Se l'URL contiene `?impersonate=<key>`, legge il payload one-time
 * da localStorage, lo applica al Redux store e rimuove il parametro dall'URL.
 * Il tab figlio diventa così una sessione indipendente come agente.
 */
const ImpersonationHandler: React.FC = () => {
  const dispatch = useDispatch();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const impersonateKey = params.get('impersonate');
    if (!impersonateKey) return;

    // Rimuovi il param dall'URL immediatamente (prima di consumare il payload)
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete('impersonate');
    window.history.replaceState({}, '', cleanUrl.toString());

    const payload = consumeImpersonationPayload(impersonateKey);
    if (!payload) return;

    // Applica la sessione impersonata al Redux store
    dispatch(
      setAuth({
        accessToken: payload.accessToken,
        refreshToken: '', // Nessun refresh per sessioni di impersonificazione
        user: payload.user,
        companies: payload.companies,
      }),
    );

    // Forza la selezione della company corretta
    if (payload.selectedCompanyId) {
      dispatch(selectCompany(payload.selectedCompanyId));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Eseguito solo al mount del tab

  return null;
};

/** Initialises Web Push subscription — montato in AgentShell (route agente). */

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 5 * 60 * 1000, // 5 minuti
            refetchOnWindowFocus: false,
            retry: 1,
            networkMode: 'offlineFirst',
        },
    },
});

const App: React.FC = () => (
    <ErrorBoundary>
        <IonApp>
                <PWAInstallProvider>
                <ImpersonationHandler />
                <GlobalErrorHandler />
                <LegacyPreferencesMigration />
                <TextScaleSync />
                <NetworkInit />
                <UpdateOfferPrompt />
                <PageLifecycleHandler />
                <TokenAutoRefresh />
                <OfflineBanner />
                <SlowConnectionBanner />
                <DraftPersistenceErrorListener />
                <StorageWarning />
                <KeyboardProvider>
                    <BrowserRouter>
                        <NavContextBridge>
                        <ProtocolLinkHandler />
                        <WindowControlsOverlaySetup />
                        <IonRouterOutlet animated={true} className="app-router-outlet">
                            <React.Suspense fallback={<SplashScreen />}>
                            <Routes>
                                {/* Public routes */}
                                <Route path="/login" element={<Login />} />
                                <Route path="/forgot-password" element={<LazyForgotPassword />} />
                                <Route path="/auth/reset-password" element={<LazyResetPassword />} />
                                <Route path="/auth/accept-invitation" element={<LazyAcceptInvitation />} />

                                {/* Protected routes */}
                                <Route
                                    path="/selectCompany"
                                    element={
                                        <AuthGuard>
                                            <LazySelectCompany />
                                        </AuthGuard>
                                    }
                                />
                                <Route
                                    path="/systemAdmin"
                                    element={
                                        <AuthGuard requiredRole="system-admin">
                                            <LazySystemAdminLayout />
                                        </AuthGuard>
                                    }
                                >
                                    <Route index element={<Navigate to="companies" replace />} />
                                    <Route path="companies/new" element={<LazySystemAdminCompanyForm />} />
                                    <Route
                                        path="companies/:companyId/edit"
                                        element={<LazySystemAdminCompanyFormEditRoute />}
                                    />
                                    <Route
                                        path="companies/:companyId/users"
                                        element={<LazySystemAdminCompanyUsersRoute />}
                                    />
                                    <Route
                                        path="companies/:companyId/agents"
                                        element={<LazySystemAdminCompanyAgentsRoute />}
                                    />
                                    <Route path="companies" element={<LazySystemAdminCompanies />} />
                                    <Route
                                        path="users"
                                        element={
                                            <LazySystemAdminPlaceholder
                                                title="Utenti"
                                                subtitle="Gestisci gli utenti del sistema"
                                            />
                                        }
                                    />
                                    <Route
                                        path="settings"
                                        element={
                                            <LazySystemAdminPlaceholder
                                                title="Impostazioni"
                                                subtitle="Configurazione piattaforma"
                                            />
                                        }
                                    />
                                    <Route
                                        path="stats"
                                        element={
                                            <LazySystemAdminPlaceholder
                                                title="Statistiche"
                                                subtitle="Metriche e report"
                                            />
                                        }
                                    />
                                    <Route path="*" element={<Navigate to="/systemAdmin/companies" replace />} />
                                </Route>
                                <Route
                                    path="/companyAdmin/*"
                                    element={
                                        <AuthGuard requiredRole="company-admin">
                                            <LazyCompanyAdmin />
                                        </AuthGuard>
                                    }
                                />
                                <Route
                                    path="/manageUsers"
                                    element={
                                        <AuthGuard>
                                            <LazyManageUsers />
                                        </AuthGuard>
                                    }
                                />
                                <Route
                                    path="/manageUser"
                                    element={
                                        <AuthGuard>
                                            <LazyManageUser />
                                        </AuthGuard>
                                    }
                                />
                                <Route
                                    path="/stats"
                                    element={
                                        <AuthGuard>
                                            <LazyStats />
                                        </AuthGuard>
                                    }
                                />
                                <Route
                                    path="/b2b"
                                    element={
                                        <AuthGuard>
                                            <LazyB2bRootRedirect />
                                        </AuthGuard>
                                    }
                                />
                                <Route
                                    path="/b2b/:companyCode/*"
                                    element={
                                        <AuthGuard requiredRole="agent">
                                            <LazyAgentShell />
                                        </AuthGuard>
                                    }
                                />
                                <Route path="/" element={<Navigate to="/login" replace />} />
                                <Route path="*" element={<LazyNotFound />} />
                            </Routes>
                            </React.Suspense>
                        </IonRouterOutlet>
                        <CustomKeyboard />
                        </NavContextBridge>
                    </BrowserRouter>
                </KeyboardProvider>
                </PWAInstallProvider>
        </IonApp>
    </ErrorBoundary>
);

const AppWithProviders: React.FC = () => {
    React.useEffect(() => {
        const unsubscribe = persistor.subscribe(() => {
            const { bootstrapped } = persistor.getState();
            if (bootstrapped) {
                unsubscribe();
                void import('./utils/authUtils').then(({ scheduleProactiveRefresh }) => {
                    const { accessToken } = store.getState().auth;
                    if (accessToken) scheduleProactiveRefresh(accessToken);
                });
            }
        });
        return unsubscribe;
    }, []);

    return (
    <QueryClientProvider client={queryClient}>
        <UpdateFlowProvider>
        <Provider store={store}>
                <TokenRefreshListener />
            <PWAUpdateProvider>
            <SyncProgressProvider>
                {/* Update UI: stesso guscio di SyncProgressOverlay (UpdateFlowShellBody) */}
                {/* UpdateFlowInit checks for updates on boot */}
                <UpdateFlowInit />
                {/* UpdateFlowBridge connects sync progress to update flow */}
                <UpdateFlowBridge />
                <PersistGateWithTimeout>
                <AppReady>
                    <App />
                </AppReady>
                </PersistGateWithTimeout>
            </SyncProgressProvider>
            </PWAUpdateProvider>
        </Provider>
        </UpdateFlowProvider>
    </QueryClientProvider>
    );
};

export default AppWithProviders;
